import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { SensorType } from '../../types/sensor-type';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { GzCheckboxWebComponent } from '../../../../ui/gzCheckbox';
import { ADOnPremSensorController } from '../controller/ADOnPremSensorController';
import { GzAccordionWebComponent } from '../../../../ui/gzAccordion';
import { GzRadioGroupWebComponent, GzRadioWebComponent } from '../../../../ui/gzRadio';
import { AccordionAlign } from '../../../../ui/gzAccordion/types';
import { GridViewWebComponent } from '../../../../grid/gridView';
import {
  Column,
  ColumnKeys,
  Domain,
  DOMAIN_CONTROLLER_STATUS,
  DomainController,
  DomainControllerRoleEnum
} from '../types';
import { ViewConfiguration, ViewType } from '../../../../grid/gridView/types';
import { getRolesTranslated, roleRenderer, statusRenderer } from './columnRenders';
import ADOnPremSensorDataProvider from '../dataProvider/ADOnPremSensorDataProvider';
import { DEFAULT_PAGE_SIZE } from '../../../integrationsGrid/types';
import { GzHardCodedFormField } from '../../../../ui/gzForm/formField/component/GzHardCodedFormField';
import { ElementClass } from '../../../../utils/htmlElement.utils';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import {
  WEB_COMPONENT_GZ_LABELED_CONTENT,
  WEB_COMPONENT_GZ_RADIO,
  WEB_COMPONENT_GZ_TOOLTIP
} from '../../../../webcomponents.constants';
import { Sensor } from '../../../common/types';
import { dateFormatter } from '@nirvana/shared/time';
import { FontSizes, FontWeights } from '../../../../types/common/types';
import { GzInfoBoxWebComponent } from '../../../../ui/gzInfoBox';
import { InfoBoxSeverity } from '../../../../ui/gzInfoBox/types';

export class ADOnPremSensorView extends BaseWebComponentView {
  public formGroup: FormGroup;
  public radioGroup: GzRadioGroupWebComponent = new GzRadioGroupWebComponent();
  public adOnpremDomainIdHardcodedField: GzHardCodedFormField;
  public domainNameHardcodedField: GzHardCodedFormField;
  public controller!: ADOnPremSensorController;
  public validSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  public valid$: Observable<boolean> = this.validSubject.pipe(distinctUntilChanged());

  public dom: {
    $wrapper: HTMLElement;
    $sensorSelectWrapper: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
    $domainList: HTMLElement;
    $noResults: HTMLElement;
    $noResultsText: HTMLElement;
    $domainListTitle: HTMLElement;
  };

  static getRequirements(dependencies: any): string {
    return `
      <div class="sensor-requirement" sensor-type="${SensorType.AD_ONPREM_DOMAIN}">
        <gz-text fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.MEDIUM}">
          ${dependencies.translateFn(
            'Before you integrate Microsoft Active Directory with GravityZone XDR, make sure the following requirements are met:'
        )}</gz-text>
        <gz-text class="text-margin-top" fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.MEDIUM}">
            ${dependencies.translateFn(
              'On a machine with the Domain Controller role, the Certificate Authority role, or both:'
        )}</gz-text>
        <ul>
          <li>${dependencies.translateFn(
            'BEST is installed and with EDR module active.'
          )}</li>
          <li>${dependencies.translateFn(
            'The Computer {GROUP_POLICIES} group policies are set to audit all login events, except for the Global Object Access Auditing policies.',
            {
              GROUP_POLICIES: `<span style="font-weight: ${FontWeights.BOLD}"> Configuration > Policies > Windows Settings > Security Settings > Advanced Audit Policy Configuration > Audit Policies</span>`
            }
          )}</li>
        </ul>
        <gz-text class="text-margin-top" fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.MEDIUM}">
          ${dependencies.translateFn(
            'On the machine with the Certificate Authority role, the following {PROPERTIES} properties are selected in the Certification Authority MMC:',
            {
              PROPERTIES: `<span style="font-weight: ${FontWeights.BOLD}"> Auditing > Events to audit</span>`
            }
          )}</gz-text>
        <ul>
        <li>${dependencies.translateFn(
          'Back up and restore the CA database'
          )}</li>
        <li>${dependencies.translateFn(
          'Change CA configuration'
        )}</li>
        <li>${dependencies.translateFn(
          'Change CA security settings'
        )}</li>
        <li>${dependencies.translateFn(
          'Issue and manage certificate requests'
        )}</li>
        <li>${dependencies.translateFn(
          'Revoke certificates and publish CRLs'
        )}</li>
        <li>${dependencies.translateFn(
          'Store and retrieve archived keys'
        )}</li>
        <li>${dependencies.translateFn(
          'Start and stop Active Directory Certificate Services'
        )}</li>
        </ul>
        <gz-text class="text-margin-top" fontSize="${FontSizes.HEADING_6}" fontWeight="${FontWeights.MEDIUM}">
          ${dependencies.translateFn(
            'For detailed instructions, refer to {ACTIVE_DIRECTORY_SENSOR}.',
            {
              ACTIVE_DIRECTORY_SENSOR: '<gz-link href="https://www.bitdefender.com/business/support/en/77211-452344-the-active-directory-sensor.html"><span>' +
                dependencies.translateFn('The Active Directory sensor') +
                '</span></gz-link>',
            }
        )}</gz-text>
      </div>
    `;
  }

  public static getSensorSpecifics(sensor: Sensor, dependencies: any) {
    return `
        ${ADOnPremSensorView.buildSensorRolesHtml(sensor, dependencies)}
        ${sensor.specifics.lastReportedDate ? ADOnPremSensorView.buildSensorLastSeenHtml(sensor, dependencies) : ''}
    `;
  }

  static getExtraInfoText(dependencies: any): string {
    return `
      <div class="extra-info-text" style="white-space: pre-wrap;font-style: italic;">
        ${dependencies.translateFn('Please allow some time for AD to start sending events to GravityZone.')}
        ${dependencies.translateFn('When ready, the integration will switch to Active status.')}
        ${dependencies.translateFn('Refresh the Sensor Management list to view the latest status.')}
      </div>
    `;
  }

  /**
   * Retrieve the total number of domain controllers
   * @param domain
   * @returns {number}
   * @private
   */
  private static getTotalNoDomainControllers(domain): number {
    if (!domain.hasOwnProperty('domainControllers')) {
      return 0;
    }

    return Object.keys(domain['domainControllers']).length;
  }

  private static buildSensorLastSeenHtml(sensor: Sensor, dependencies: any): string {
    return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${dependencies.translateFn('Last reported')}" hasColon="true">
                ${ADOnPremSensorView.computeSensorLastReportedLabel(sensor, dependencies)}
            </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
  }

  private static buildSensorRolesHtml(sensor: Sensor, dependencies: any): string {
    const roles = getRolesTranslated(sensor.specifics.roles as DomainControllerRoleEnum[]);
    return `<${WEB_COMPONENT_GZ_LABELED_CONTENT} label="${dependencies.translateFn('Roles')}" hasColon="true">
                <${WEB_COMPONENT_GZ_TOOLTIP} class="tooltip-labeled-value" fontSize="${FontSizes.PARAGRAPH_12}" tooltipText="${roles}" isellipsis="true">
                    ${roles}
                </${WEB_COMPONENT_GZ_TOOLTIP}>
            </${WEB_COMPONENT_GZ_LABELED_CONTENT}>`;
  }

  private static computeSensorLastReportedLabel(sensor: Sensor, dependencies: any): string {
    let dateString = '';
    if (sensor.specifics.lastReportedDate) {
      const event = Date.parse(sensor.specifics.lastReportedDate);
      dateString = dateFormatter(event, dependencies.getTimezone(), 'DD MMM YYYY, HH:mm');
    }

    return dateString;
  }

  getDomElements(): void {
    this.dom = {
      $wrapper: this.shadow.querySelector('.form-wrapper'),
      $sensorSelectWrapper: this.shadow.querySelector('.sensor-select-wrapper'),
      $domainList: this.shadow.querySelector('#domain_container'),
      $noResults: this.shadow.querySelector('#no-results'),
      $noResultsText: this.shadow.querySelector('#no-results-text'),
      $domainListTitle: this.shadow.querySelector('#domain-list-title'),
    };
    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations(): void {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getSensorsIntegrationsStrings();

    this.dom.$domainListTitle.innerText = this.dependencies.translateFn('Domains');
  }

  setTranslatedStrings() {
    const sensorType = SensorType.AD_ONPREM_DOMAIN;
    const typeLabel =
      typeof this.translations.sensorNames[sensorType] !== 'undefined'
        ? this.translations.sensorNames[sensorType]
        : this.translations.unknownSensorTypeLabel;
  }

  protected createFormGroup() {
    this.adOnpremDomainIdHardcodedField = new GzHardCodedFormField<string>('');
    this.domainNameHardcodedField = new GzHardCodedFormField<string>('');

    this.formGroup = new FormGroup({
      adOnpremDomainId: this.adOnpremDomainIdHardcodedField,
      domainName: this.domainNameHardcodedField,
    });
  }

  protected setViewData(viewData) {
    return;
  }

  public resetData(): void {
    return;
  }

  buildView(viewData): void {
    this.buildSuccessView(viewData);
  }

  public buildSuccessView(viewData): void {
    this.setTranslatedStrings();
    this.initForm();
    this.createFormGroup();
  }

  private getView(uniqId): ViewConfiguration<Column, ColumnKeys> {
    return {
      id: 'ad-sensor-grid' + uniqId,
      name: 'Default view',
      type: ViewType.DEFAULT,
      filters: [],
      displayedColumns: [0, 1, 2],
      pageSize: DEFAULT_PAGE_SIZE,
      compactView: false,
    };
  }

  private getColumnDetails() {
    return [
      {
        column: Column.NAME,
        key: ColumnKeys.NAME,
        columnName: this.dependencies.translateFn('Hostname'),
        type: 2,
        removable: false,
      },
      {
        column: Column.STATUS,
        key: ColumnKeys.STATUS,
        columnName: this.dependencies.translateFn('Status'),
        type: 2,
        removable: false,
      },
      {
        column: Column.ROLE,
        key: ColumnKeys.ROLE,
        columnName: this.dependencies.translateFn('Roles'),
        type: 2,
        removable: false,
      },
    ];
  }

  private createDomainConfigTable(
    domain: Domain,
    dataProvider: ADOnPremSensorDataProvider
  ): GridViewWebComponent<Column, ColumnKeys> {
    const numberOfRows = domain.domainControllers !== undefined ? domain.domainControllers.length : 0;
    const headerHeight = 57;
    const domainControllerHeight = 52;
    const footerHeight = 15;
    const table = new GridViewWebComponent() as GridViewWebComponent<Column, ColumnKeys>;

    table.dataProvider = dataProvider;

    const view: ViewConfiguration<Column, ColumnKeys> = this.getView(domain._id);

    table.viewId = view.id;
    table.filters = view.filters;
    table.columns = view.displayedColumns;
    table.pageSize = view.pageSize;
    table.isCompactViewChecked = view.compactView;
    table.allColumnDetails = this.getColumnDetails();
    table.predefinedFiltersKeys = [];
    table.removeFilters = true;
    table.hideFiltersToggle = true;
    table.disableGridSettings = true;
    table.hideHeaderActions = true;
    table.hideFooter = true;
    table.enableRowClickEvent = true;
    table.hideGridRefresh = true;
    table.hideFiltersToggle = true;
    table.dataProvider = dataProvider;

    table.columnRenderers = {
      [ColumnKeys.STATUS]: statusRenderer.bind(this),
      [ColumnKeys.ROLE]: roleRenderer.bind(this),
    };
    table.style.height = (headerHeight + numberOfRows * domainControllerHeight + footerHeight).toString() + 'px';

    return table;
  }

  getWarningWrapper() {
    const gzInfoBox = new GzInfoBoxWebComponent();
    gzInfoBox.severity = InfoBoxSeverity.WARNING;
    gzInfoBox.innerHTML = this.dependencies.translateFn('<i>All hosts must have BEST with EDR installed.<br>To take response actions, the selected domain must have at least one online domain controller that has BEST with EDR installed.</i>');
    return gzInfoBox;
  }

  private createRadio(domain: Domain): GzRadioWebComponent {
    const totalNoDC = ADOnPremSensorView.getTotalNoDomainControllers(domain);
    const radio = new GzRadioWebComponent();
    radio.setAttribute('domainId', domain._id);
    radio.setAttribute('domainName', domain.domainName);
    radio.label = ' ( ' + this.getNoActiveDomainControllers(domain) + '/' + totalNoDC + ' ) ' + domain.domainName;
    radio.value = domain._id;
    radio.highlightOnCheck = true;
    radio.setAttribute('total-no-dc', totalNoDC.toString());
    if (domain.domainControllers?.length === 0) {
      radio.disabled = true;
      radio.style.padding = '9px 0px 9px 9px';
    }
    return radio;
  }

  private createAccordion(domain: Domain, grid: GridViewWebComponent<Column, ColumnKeys>): GzAccordionWebComponent {
    const wrapper = document.createElement('span');
    wrapper.setAttribute('slot', 'dynamic-title-content');
    wrapper.appendChild(this.createRadio(domain));
    const accordion = new GzAccordionWebComponent();
    accordion.setAttribute('domainId', domain._id);
    accordion.setAttribute('domainGuid', domain.domainGuid);
    accordion.setAttribute('domainName', domain.domainName);
    accordion.align = AccordionAlign.LEFT;
    accordion.appendChild(grid);
    accordion.appendChild(wrapper);
    accordion.appendChild(this.getWarningWrapper());

    accordion.addEventListener('click', async (e: CustomEvent) => {
      const target = e.target as Element;
      if (target instanceof GzAccordionWebComponent || target instanceof GzRadioWebComponent) {
        const domainId = target.getAttribute('domainId') as string;
        const domainName = target.getAttribute('domainName') as string;

        const radioGroup = this.shadow.querySelector('gz-radio-group') as GzRadioGroupWebComponent;
        radioGroup.selectOption(domainId);

        this.adOnpremDomainIdHardcodedField.setValue(domainId);
        this.domainNameHardcodedField.setValue(domainName);
        this.validSubject.next(true);
      }
    });

    return accordion;
  }

  private initGrids(grids: GridViewWebComponent<Column, ColumnKeys>[]) {
    grids.forEach(grid => {
      if (grid.injectDependencies) {
        grid.injectDependencies(this.dependencies);
        grid.start().then().catch();
      }
    });
  }

  public initForm() {
    const dataProvider = new ADOnPremSensorDataProvider(this.dependencies);
    const domainsPromise = dataProvider.getDomainData();
    this.validSubject.next(false);
    domainsPromise
      .then(domains => {
        const grids: GridViewWebComponent<Column, ColumnKeys>[] = [];

        if (domains.length) {
          this.dom.$noResults.classList.add(ElementClass.HIDDEN);
          domains.forEach(domain => {
            const tableDataProvider = new ADOnPremSensorDataProvider(this.dependencies, domains, domain['_id']);
            if (domain.domainControllers?.length === 0) {
              // add just a radio, instead of accordion, in order to not show the dropdown icon
              this.radioGroup.appendChild(this.createRadio(domain));
            } else {
              const grid = this.createDomainConfigTable(domain, tableDataProvider);
              grids.push(grid);
              this.radioGroup.appendChild(this.createAccordion(domain, grid));
            }
          });
          this.dom.$domainList.appendChild(this.radioGroup);
          this.initGrids(grids);
        } else {
          this.dom.$noResultsText.innerText = this.dependencies.translateFn('No domains enabled.');
          this.dom.$noResultsText.innerText +=
            '\n' + this.dependencies.translateFn('Start by deploying BEST with EDR on a domain controller.');

          this.dom.$noResults.classList.remove(ElementClass.HIDDEN);
        }
      })
      .catch(e => {
        console.log(e);
      });
  }

  /**
   * Retrieve the number of active domain controllers
   * @param domain
   * @returns {number}
   * @private
   */
  private getNoActiveDomainControllers(domain: Domain): number {
    if (!domain.hasOwnProperty('domainControllers')) {
      return 0;
    }

    const domainControllers = domain['domainControllers'] as DomainController[];
    const activeDomainControllers = domainControllers.filter(domainController => {
      return domainController.status === DOMAIN_CONTROLLER_STATUS.READY;
    });

    return activeDomainControllers.length;
  }

  /**
   * Disable domains without Domain Controllers
   */
  public disableDomainsWithoutDC(): void {
    const radios = Array.from(this.dom.$domainList.querySelectorAll(WEB_COMPONENT_GZ_RADIO)) as GzRadioWebComponent[];
    radios.forEach((radio: GzRadioWebComponent, index) => {
      if (radio.getAttribute('total-no-dc') === '0') {
        radio.setAttribute('disabled', 'true');
      }
    });
  }

  public setEnabledAllFields(isEnabled: boolean): void {
    this.radioGroup.setEnabled(isEnabled);
    if (isEnabled) {
      this.radioGroup.updateComplete.then(() => {
        this.disableDomainsWithoutDC();
      });
    }
  }
}
